import { useLocalStorage } from '@mantine/hooks';
import { useEffect } from 'react';
import { fetchApiAllItems, useApiQuery } from '../api';
import { useDashboard, useTeam, useTeamPermission } from '../hooks';

export const STORYBOOK_ACCOUNTING_CONNECTIONS_QUERY_KEY =
  'onboarding-accounting-connections';

type AccountingConnectionPlatform = {
  id: string;
};

export function resolveAccountingConnectionStorageUpdate({
  accounting,
  accountingPlatforms,
  isLoading,
}: {
  accounting: string | undefined;
  accountingPlatforms: AccountingConnectionPlatform[];
  isLoading: boolean;
}): string | null {
  if (isLoading || accountingPlatforms.length === 0) return null;

  if (accountingPlatforms.some((platform) => platform.id === accounting)) {
    return null;
  }

  return accountingPlatforms[0]?.id ?? null;
}

function useConnectionsQuery() {
  const [{ id: teamId }] = useTeam();
  const [dashboard] = useDashboard();
  // This query is the shared entry point for accounting-connection state, so
  // gating it here keeps every caller — navigation, issue badges, automation
  // editors — from issuing a forbidden read for a member without connection
  // access.
  const canReadConnections = useTeamPermission('integrations:read');

  return useApiQuery(
    async (api, signal) => {
      const connections = await fetchApiAllItems(async (page) => {
        const result = await api.GET('/connections', {
          params: {
            query: {
              appCategory: 'accountingPlatform',
              page,
              status: 'active',
            },
          },
          signal,
        });

        if (result.error) throw result.error;

        return {
          hasNextPage: !!result.data.pagination.nextPage,
          items: result.data.data,
        };
      });

      return connections
        .sort((left, right) => left.app.id.localeCompare(right.app.id))
        .map((connection) => ({
          appId: connection.app.id,
          name: connection.name,
          type: connection.app.type,
          category: connection.app.type,
          id: connection.id,
          icon: connection.app.icon,
        }));
    },
    {
      skip: !canReadConnections || dashboard !== 'propertyManager' || !teamId,
      queryGroup: 'connections',
      queryKeys: ['connections'],
      uniqueQueryKey: 'accounting-connections',
      storybookQueryKey: STORYBOOK_ACCOUNTING_CONNECTIONS_QUERY_KEY,
      keepPreviousData: true,
      // Writes invalidate the `connections` group, which marks this stale.
      staleTime: 5 * 60_000,
      variables: {
        teamId,
      },
    }
  );
}

export function useAccountingConnectionQuery() {
  const [{ id: teamId }] = useTeam();
  const { data: accountingPlatforms = [], ...queryData } =
    useConnectionsQuery();

  const [accounting, setAccounting] = useLocalStorage<string | undefined>({
    key: `${teamId}_acc`,
    defaultValue: accountingPlatforms[0]?.id,
  });

  useEffect(() => {
    const nextAccounting = resolveAccountingConnectionStorageUpdate({
      accounting,
      accountingPlatforms,
      isLoading: queryData.isLoading,
    });

    if (nextAccounting !== null) setAccounting(nextAccounting);
  }, [accountingPlatforms, accounting, queryData.isLoading, setAccounting]);

  return {
    accounting: accountingPlatforms.find(
      (platform) => platform.id === accounting
    ),
    setAccounting,
    accountingPlatforms,
    loading: queryData.isLoading,
  };
}
